/**
 * TemplateClickPlugin - Template click handling for TinyMCE
 *
 * Handles:
 * - Making template elements clickable
 * - Dispatching template-selected events to show properties panel
 * - Maintaining template element registry for property updates
 *
 * Ported from: _archived_custom_editor/RichTextLoader.js
 */

export class TemplateClickPlugin {
    constructor(editor) {
        this.editor = editor;

        // Use global template element map for cross-system compatibility
        if (!window.templateElementMap) {
            window.templateElementMap = {};
        }
        this.templateElementMap = window.templateElementMap;
    }

    /**
     * Initialize the plugin
     */
    init() {
        console.log('🖱️  TemplateClickPlugin initializing for editor:', this.editor.id);

        this.attachClickHandlers();
        this.attachContextMenu();

        console.log('✅ TemplateClickPlugin initialized');
    }

    /**
     * Attach click handlers to editor
     */
    attachClickHandlers() {
        const editorBody = this.editor.getBody();

        // Use TinyMCE's click event system
        this.editor.on('click', (e) => {
            const clickedElement = e.target;

            // Walk up the DOM tree to find a template wrapper
            let currentElement = clickedElement;
            while (currentElement && currentElement !== editorBody) {
                // Check for template by data-template-id attribute OR by ID pattern (template-{timestamp}-{random})
                const hasTemplateAttribute = currentElement.hasAttribute && currentElement.hasAttribute('data-template-id');
                const hasTemplateId = currentElement.id && currentElement.id.startsWith('template-');

                if (hasTemplateAttribute || hasTemplateId) {
                    const templateId = currentElement.getAttribute('data-template-id') || 'legacy-template';
                    const instanceId = currentElement.id;
                    const templateName = currentElement.getAttribute('data-template-name') || 'Template';

                    // Get the style attribute of the clicked element (not the wrapper)
                    const currentElementStyle = clickedElement.getAttribute('style') || '';
                    const currentElementTag = clickedElement.tagName ? clickedElement.tagName.toLowerCase() : '';

                    console.log('🎯 Template clicked:', {
                        instanceId,
                        templateId,
                        templateName,
                        currentElementTag
                    });

                    // Dispatch event that Settings.js listens for
                    // Use document.dispatchEvent for global event bubbling
                    document.dispatchEvent(new CustomEvent('template-selected', {
                        detail: {
                            instanceId: instanceId,
                            templateId: templateId,
                            templateName: templateName,
                            element: clickedElement,
                            currentElement: clickedElement,
                            currentElementStyle: currentElementStyle,
                            currentElementTag: currentElementTag,
                            clickedElement: clickedElement
                        },
                        bubbles: true
                    }));

                    break;
                }
                currentElement = currentElement.parentNode;
            }
        });

        // Double-click a block in the canvas → open the slide-in Blocks sidebar (if closed)
        // and show that block's Properties. The single-click handler above already dispatches
        // `template-selected` (harmless when the panel is closed); double-click guarantees the
        // panel is open before selecting.
        this.editor.on('dblclick', (e) => {
            const clickedElement = e.target;

            let currentElement = clickedElement;
            while (currentElement && currentElement !== editorBody) {
                const hasTemplateAttribute = currentElement.hasAttribute && currentElement.hasAttribute('data-template-id');
                const hasTemplateId = currentElement.id && currentElement.id.startsWith('template-');

                if (hasTemplateAttribute || hasTemplateId) {
                    const templateId = currentElement.getAttribute('data-template-id') || 'legacy-template';
                    const instanceId = currentElement.id;
                    const templateName = currentElement.getAttribute('data-template-name') || 'Template';

                    // Ensure the slide-in sidebar is open.
                    if (window.__richTextHandlers && window.__richTextHandlers[this.editor.id]) {
                        window.__richTextHandlers[this.editor.id]('ensureBlocksSidebarOpen');
                    }

                    // Select after a short delay so a freshly-created panel's listener is live.
                    setTimeout(() => {
                        document.dispatchEvent(new CustomEvent('template-selected', {
                            detail: {
                                instanceId,
                                templateId,
                                templateName,
                                element: clickedElement,
                                clickedElement
                            },
                            bubbles: true
                        }));
                    }, 80);

                    break;
                }
                currentElement = currentElement.parentNode;
            }
        });

        console.log('👆 Template click handlers attached');
    }

    /**
     * Walk up from an element to the nearest block wrapper inside this editor.
     * Same test the click handlers use: data-template-id attribute or `template-*` id.
     * @param {HTMLElement} el - Starting element
     * @returns {HTMLElement|null} Block wrapper, or null when not inside a block
     */
    findBlockWrapper(el) {
        const editorBody = this.editor.getBody();
        let current = el;
        while (current && current !== editorBody) {
            const hasTemplateAttribute = current.hasAttribute && current.hasAttribute('data-template-id');
            const hasTemplateId = current.id && current.id.startsWith('template-');
            if (hasTemplateAttribute || hasTemplateId) return current;
            current = current.parentNode;
        }
        return null;
    }

    /**
     * Right-click a block in the canvas → small custom menu with "Delete Block".
     * Bound in the capture phase on the iframe document so TinyMCE's own context
     * menu (link/image/table) never opens over the top of ours.
     */
    attachContextMenu() {
        const doc = this.editor.getDoc();

        doc.addEventListener('contextmenu', (e) => {
            const wrapper = this.findBlockWrapper(e.target);
            if (!wrapper) {
                this.closeContextMenu();
                return; // not a block — leave the default/TinyMCE menu alone
            }

            e.preventDefault();
            e.stopPropagation();

            // Select the block too, so the properties panel follows the right-click.
            document.dispatchEvent(new CustomEvent('template-selected', {
                detail: {
                    instanceId: wrapper.id,
                    templateId: wrapper.getAttribute('data-template-id') || 'legacy-template',
                    templateName: wrapper.getAttribute('data-template-name') || 'Template',
                    element: e.target,
                    clickedElement: e.target
                },
                bubbles: true
            }));

            this.showContextMenu(e, wrapper);
        }, true);
    }

    /**
     * Render the block context menu in the top document, positioned at the cursor.
     * @param {MouseEvent} e - contextmenu event (coords are iframe-relative)
     * @param {HTMLElement} wrapper - Block wrapper the menu acts on
     */
    showContextMenu(e, wrapper) {
        this.closeContextMenu();

        // Translate iframe coordinates into top-document page coordinates.
        const iframe = this.editor.iframeElement;
        const rect = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };
        const x = rect.left + e.clientX + window.scrollX;
        const y = rect.top + e.clientY + window.scrollY;

        const menu = document.createElement('div');
        menu.className = 'wps-block-context-menu';
        menu.setAttribute('style', [
            'position: absolute',
            `left: ${x}px`,
            `top: ${y}px`,
            'z-index: 100000',
            'min-width: 160px',
            'padding: 0.25rem',
            'background: #ffffff',
            'border: 1px solid #e5e7eb',
            'border-radius: 0.375rem',
            'box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
            'font-family: system-ui, sans-serif',
            'font-size: 0.8rem'
        ].join(';'));

        const label = wrapper.getAttribute('data-template-name') || 'Block';
        const heading = document.createElement('div');
        heading.textContent = label;
        heading.setAttribute('style', 'padding: 0.35rem 0.5rem; color: #6b7280; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em;');
        menu.appendChild(heading);

        const deleteItem = document.createElement('button');
        deleteItem.type = 'button';
        deleteItem.textContent = 'Delete Block';
        deleteItem.setAttribute('style', 'display: block; width: 100%; text-align: left; padding: 0.4rem 0.5rem; border: none; background: transparent; color: #dc2626; border-radius: 0.25rem; cursor: pointer; font-size: 0.8rem;');
        deleteItem.addEventListener('mouseenter', () => { deleteItem.style.background = '#fee2e2'; });
        deleteItem.addEventListener('mouseleave', () => { deleteItem.style.background = 'transparent'; });
        // mousedown, not click: the outside-click dismisser fires on mousedown and
        // would tear the menu down before a click ever lands on this button.
        deleteItem.addEventListener('mousedown', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this.closeContextMenu();
            this.deleteBlock(wrapper);
        });
        menu.appendChild(deleteItem);

        document.body.appendChild(menu);
        this.contextMenu = menu;

        // Keep the menu inside the viewport.
        const menuRect = menu.getBoundingClientRect();
        if (menuRect.right > window.innerWidth) {
            menu.style.left = `${Math.max(0, x - menuRect.width)}px`;
        }
        if (menuRect.bottom > window.innerHeight) {
            menu.style.top = `${Math.max(0, y - menuRect.height)}px`;
        }

        // Dismiss on any click / scroll / Escape, inside the iframe or out.
        this.closeContextMenuHandler = (ev) => {
            if (ev && ev.target && menu.contains(ev.target)) return; // clicks inside the menu
            this.closeContextMenu();
        };
        this.escapeHandler = (ev) => { if (ev.key === 'Escape') this.closeContextMenu(); };
        setTimeout(() => {
            document.addEventListener('mousedown', this.closeContextMenuHandler);
            document.addEventListener('scroll', this.closeContextMenuHandler, true);
            document.addEventListener('keydown', this.escapeHandler);
            this.editor.getDoc().addEventListener('mousedown', this.closeContextMenuHandler);
        }, 0);
    }

    /**
     * Remove the context menu and its dismiss listeners (safe to call any time).
     */
    closeContextMenu() {
        if (this.contextMenu) {
            this.contextMenu.remove();
            this.contextMenu = null;
        }
        if (this.closeContextMenuHandler) {
            document.removeEventListener('mousedown', this.closeContextMenuHandler);
            document.removeEventListener('scroll', this.closeContextMenuHandler, true);
            const doc = this.editor.getDoc && this.editor.getDoc();
            if (doc) doc.removeEventListener('mousedown', this.closeContextMenuHandler);
            this.closeContextMenuHandler = null;
        }
        if (this.escapeHandler) {
            document.removeEventListener('keydown', this.escapeHandler);
            this.escapeHandler = null;
        }
    }

    /**
     * Delete a block — the same confirm-then-remove the sidebar's "Delete Block"
     * button performs. Announces `template-deleted` so an open properties panel
     * can clear its selection.
     * @param {HTMLElement} wrapper - Block wrapper to remove
     */
    deleteBlock(wrapper) {
        if (!wrapper) return;
        if (!confirm('Delete this block?')) return;

        wrapper.remove();

        // Keep TinyMCE's undo stack and dirty flag in sync with the DOM change.
        if (this.editor.undoManager) this.editor.undoManager.add();
        this.editor.setDirty(true);
        this.editor.fire('change');

        document.dispatchEvent(new CustomEvent('template-deleted', {
            detail: { element: wrapper },
            bubbles: true
        }));
    }

    /**
     * Register a template element in the global registry
     * @param {HTMLElement} wrapper - Template wrapper element
     * @param {string} toolType - Tool type (e.g., 'WYSIWYG')
     * @param {Object} toolInstance - Optional tool instance reference
     */
    registerTemplateElement(wrapper, toolType, toolInstance = null) {
        const instanceId = wrapper.getAttribute('data-template-instance') ||
                           wrapper.getAttribute('data-instance-id') ||
                           wrapper.id;

        if (!instanceId) {
            console.warn('⚠️  Template wrapper missing instance ID', wrapper);
            return;
        }

        this.templateElementMap[instanceId] = {
            element: wrapper,
            toolType: toolType,
            toolInstance: toolInstance
        };

        console.log('📋 Registered template element:', instanceId, toolType);
    }

    /**
     * Unregister a template element from the registry
     * @param {string} instanceId - Instance ID to unregister
     */
    unregisterTemplateElement(instanceId) {
        if (this.templateElementMap[instanceId]) {
            delete this.templateElementMap[instanceId];
            console.log('🗑️  Unregistered template element:', instanceId);
        }
    }

    /**
     * Get template element from registry
     * @param {string} instanceId - Instance ID
     * @returns {Object|null} Template element data
     */
    getTemplateElement(instanceId) {
        return this.templateElementMap[instanceId] || null;
    }

    /**
     * Cleanup plugin resources
     */
    destroy() {
        this.closeContextMenu();
        // Clean up template elements for this editor
        // (Keep global map but remove this editor's templates if needed)
        console.log('🗑️  TemplateClickPlugin destroyed');
    }
}
